export interface AgentSkillFrontmatter {
    license?: string | null;
    compatibility?: string | null;
    allowedTools?: string | null;
    metadata?: Record<string, string>;
}

export interface AgentSkillFile {
    relativePath: string;
    body: string;
    frontmatter?: AgentSkillFrontmatter | null;
}

export interface AgentSkillToolDefinition {
    name: string;
    friendlyName: string;
    description: string;
}

export interface AgentSkillTestCase {
    userRequest: string;
    idealOutcome: string;
    expectedToolCalls?: string | null;
}

export type AgentSkillScope = "KboChat" | "KboFlow";

export interface SaveAgentSkillRequest {
    name: string;
    description: string;
    skillText: string;
    isPublished: boolean;
    scopes: AgentSkillScope[];
    frontmatter?: AgentSkillFrontmatter | null;
    files: AgentSkillFile[];
    testCases?: AgentSkillTestCase[] | null;
    configuration?: Record<string, unknown> | null;
}

export interface AgentSkillListItem {
    id: string;
    name: string;
    description: string;
    fileCount: number;
    isPublished: boolean;
    scopes: AgentSkillScope[];
    isInLibrary: boolean;
    createdOn: string;
    createdBy: string;
    modifiedOn?: string | null;
    modifiedBy?: string | null;
}

export interface AgentSkillHistoryItem {
    id: string;
    name: string;
    isActive: boolean;
    isInLibrary: boolean;
    createdOn: string;
    createdBy: string;
    modifiedOn?: string | null;
    modifiedBy?: string | null;
}

export interface AgentSkillLibraryItem {
    id: string;
    skillVersionGroupId?: string | null;
    tenantSkillId?: string | null;
    status?: AgentSkillLibraryStatus | null;
    restoresLibraryVersionFromHistory?: boolean;
    name: string;
    description: string;
    fileCount: number;
    scopes: AgentSkillScope[];
    modifiedOn?: string | null;
    existsInTenant: boolean;
}

export type AgentSkillLibraryStatus =
    | "Missing"
    | "CurrentLibraryVersion"
    | "DifferentVersion"
    | "TenantOwned"
    | "NameConflict"
    | "DuplicateActiveVersions"
    | "HistoricalIdConflict"
    | "Legacy";

export interface AgentSkillSyncOrganization {
    id: string;
    name: string;
}

export interface AgentSkillSyncOrganizationsResponse {
    organizations: AgentSkillSyncOrganization[];
}

export interface AgentSkillSyncPreviewRequest {
    organizationIds: string[];
    skillVersionGroupIds: string[];
}

export type AgentSkillSyncPreviewStatus =
    | "Missing"
    | "CurrentLibraryVersion"
    | "DifferentVersion"
    | "TenantOwned"
    | "NameConflict"
    | "DuplicateActiveVersions"
    | "HistoricalIdConflict";

export interface AgentSkillSyncPreviewItem {
    skillVersionGroupId: string;
    name: string;
    librarySkillId: string;
    libraryModifiedOn?: string | null;
    tenantSkillId?: string | null;
    tenantModifiedOn?: string | null;
    status: AgentSkillSyncPreviewStatus;
}

export interface AgentSkillSyncOrganizationPreview {
    organizationId: string;
    organizationName: string;
    skills: AgentSkillSyncPreviewItem[];
}

export interface AgentSkillSyncPreviewResponse {
    organizations: AgentSkillSyncOrganizationPreview[];
}

export interface AgentSkillSyncSkillRequest {
    skillVersionGroupId: string;
    shouldCreate: boolean;
    shouldOverwrite: boolean;
}

export interface AgentSkillSyncOrganizationRequest {
    organizationId: string;
    syncRequests: AgentSkillSyncSkillRequest[];
}

export interface AgentSkillSyncRequest {
    organizationSyncRequests: AgentSkillSyncOrganizationRequest[];
}

export interface AgentSkillSyncResultItem {
    skillVersionGroupId: string;
    tenantSkillId?: string | null;
    status: AgentSkillSyncResultStatus;
}

export type AgentSkillSyncResultStatus =
    | AgentSkillLibraryStatus
    | "Created"
    | "Updated"
    | "Restored"
    | "DifferentLibraryVersion";

export interface AgentSkillSyncOrganizationResult {
    organizationId: string;
    skills: AgentSkillSyncResultItem[];
}

export interface AgentSkillSyncResponse {
    organizations: AgentSkillSyncOrganizationResult[];
}

export interface AgentSkill {
    id: string;
    name: string;
    description: string;
    skillText: string;
    isPublished: boolean;
    scopes: AgentSkillScope[];
    frontmatter?: AgentSkillFrontmatter | null;
    files: AgentSkillFile[];
    testCases?: AgentSkillTestCase[] | null;
    configuration?: Record<string, unknown> | null;
    createdOn: string;
    createdBy: string;
    modifiedOn?: string | null;
    modifiedBy?: string | null;
}

export interface AgentSkillTestDatasetImportResponse {
    testCases: AgentSkillTestCase[];
    warnings: string[];
}

export interface SaveAgentSkillTestDatasetRequest {
    testCases: AgentSkillTestCase[];
}

export interface AgentSkillTestRunRequest {
    skill: SaveAgentSkillRequest;
    testCases: AgentSkillTestCase[];
}

export interface AgentSkillInlineTestRequest {
    skill: SaveAgentSkillRequest;
    userRequest: string;
}

export interface AgentSkillTestRunResponse {
    passedCount: number;
    failedCount: number;
    results: AgentSkillTestCaseResult[];
}

export interface AgentSkillTestCaseResult {
    index: number;
    passed: boolean;
    summary: string;
    suggestions: string[];
}

export function emptySaveAgentSkillRequest(): SaveAgentSkillRequest {
    return {
        name: "",
        description: "",
        skillText: "",
        isPublished: false,
        scopes: [],
        frontmatter: { allowedTools: "", metadata: {} },
        files: [],
        testCases: [],
        configuration: {},
    };
}
